import { parseLayerFilename } from '../../shared/layer-naming'
import type { LayerUrls } from '../../shared/types'

export interface TryOnFiles {
  layers: LayerUrls
  urls: string[]
}

export interface ObjectUrlMaker {
  create: (file: Blob) => string
  revoke: (url: string) => void
}

const browserUrls: ObjectUrlMaker = {
  create: (file) => URL.createObjectURL(file),
  revoke: (url) => URL.revokeObjectURL(url),
}

/**
 * The picked files as a layer map, so `composeAvatar` can render them exactly as it renders a
 * registered option. This is what makes try-on-before-upload possible: no byte leaves the browser.
 *
 * Every URL is returned so the caller can revoke them — an object URL held after the dialog closes
 * pins the whole file in memory for the life of the document.
 */
export function tryOnLayersFrom(
  files: File[],
  urls: ObjectUrlMaker = browserUrls,
): TryOnFiles {
  const layers: LayerUrls = {}
  const created: string[] = []

  files.forEach((file) => {
    const parsed = parseLayerFilename(file.name)
    if (!parsed.ok) {
      return
    }
    // No object URL, no try-on: the caller shows the registered options instead of crashing the
    // page it is a dialog on.
    const url = made(file, urls)
    if (!url) {
      return
    }
    created.push(url)
    layers[parsed.role] = { png: url }
  })

  return { layers, urls: created }
}

function made(file: Blob, urls: ObjectUrlMaker): string | undefined {
  try {
    return urls.create(file)
  } catch {
    return undefined
  }
}

export function revokeAll(urls: string[], maker: ObjectUrlMaker = browserUrls): void {
  urls.forEach((url) => maker.revoke(url))
}
